// src/screens/PendingMatchesScreen.js
// Your Events — clean, simple view of all your matches and tournaments

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
  TextInput,
  FlatList,
  Share,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { fmtCrits } from '../utils/currencyHelper';
import useCritsEnabled from '../hooks/useCritsEnabled';

import { auth, db } from '../../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  collectionGroup,
  query,
  where,
  limit,
  onSnapshot,
  getDoc,
  doc,
} from 'firebase/firestore';

// =============================================================================
// COLORS
// =============================================================================
const COLORS = {
  BG: '#0A0A0A',
  CARD: '#131313',
  BORDER: '#262626',
  GOLD: '#E2B714',
  TEXT: '#EEEEEE',
  MUTED: '#9AA0A6',
  GREEN: '#2ECC71',
  RED: '#E74C3C',
  BLUE: '#3498DB',
  ORANGE: '#F39C12',
};

const PAGE_SIZE = 100;

// =============================================================================
// HELPERS
// =============================================================================
const lc = (v) => String(v ?? '').toLowerCase();

const toMillis = (tsLike) => {
  if (!tsLike) return null;
  if (typeof tsLike === 'number') return tsLike;
  if (tsLike.toMillis) return tsLike.toMillis();
  if (tsLike.seconds) return tsLike.seconds * 1000;
  if (tsLike._seconds) return tsLike._seconds * 1000;
  const parsed = Date.parse(tsLike);
  return isNaN(parsed) ? null : parsed;
};

const isLive = (item) => lc(item?.status) === 'live';
const isCanceled = (item) => lc(item?.status) === 'canceled';
const isFinished = (item) =>
  ['finished', 'complete', 'completed', 'past'].includes(lc(item?.status));

const isUpcoming = (item) => {
  const t = item?.startAt || item?.startTime || item?.scheduledAt || item?.startDate;
  const ms = toMillis(t);
  return Number.isFinite(ms) && ms > Date.now();
};

const bucketOf = (item) => {
  if (isCanceled(item)) return 'canceled';
  if (isFinished(item)) return 'past';
  if (isLive(item)) return 'live';
  if (isUpcoming(item)) return 'upcoming';
  return 'pending';
};

const formatWhen = (tsLike) => {
  const ms = toMillis(tsLike);
  if (!ms) return '';
  const d = new Date(ms);
  return d.toLocaleString([], { 
    month: 'short', 
    day: 'numeric', 
    hour: 'numeric', 
    minute: '2-digit' 
  });
};

const usd = (cents) => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD' 
  }).format(n / 100);
};

const titleOf = (it) =>
  it?.title ||
  it?.name ||
  `${(it?.gameName || it?.gameId || 'Event').toString()} ${it?.mode ? String(it.mode).toUpperCase() : ''}`.trim();

function dedupByKindId(arr) {
  const m = new Map();
  for (const it of arr || []) {
    const k = `${it.kind || 'x'}_${it.id}`;
    if (!m.has(k)) m.set(k, it);
    else m.set(k, { ...m.get(k), ...it });
  }
  return Array.from(m.values());
}

const badgeColors = {
  live: COLORS.GREEN,
  pending: COLORS.ORANGE,
  upcoming: COLORS.BLUE,
  past: COLORS.MUTED,
  canceled: COLORS.RED,
};

// =============================================================================
// COUNTDOWN HOOK
// =============================================================================
function useCountdown(targetMs) {
  const [now, setNow] = useState(Date.now());
  
  useEffect(() => {
    if (!targetMs || targetMs <= Date.now()) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetMs]);
  
  if (!targetMs) return null;
  
  const diff = Math.max(0, targetMs - now);
  if (diff <= 0) return null;
  
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const mins = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000));
  const secs = Math.floor((diff % (60 * 1000)) / 1000);
  
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m ${secs}s`;
}

// =============================================================================
// EVENT CARD COMPONENT
// =============================================================================
// critsEnabled (evergreen 2.3) comes from the screen's single listener and
// defaults TRUE so an unpassed prop can never hide Crit items in ON mode.
function EventCard({ item, userId, onPress, onShare, critsEnabled = true }) {
  const bucket = bucketOf(item);
  const startMs = toMillis(item?.startAt || item?.startTime || item?.scheduledAt);
  const countdown = useCountdown(bucket === 'upcoming' ? startMs : null);
  
  // Crit events (1 Crit = $1) show entry/prize/earnings in Crits; cash in $.
  const isCrit = (item?.entryCurrency === 'crits') || (item?.critEntryCost != null);
  const fmtMoney = (v) => (isCrit ? fmtCrits(v) : usd(v));
  const prize = isCrit
    ? (item?.critPotCount ?? null)
    : (item?.prizePoolCents ?? item?.netPoolCents ?? item?.guaranteedPoolCents ?? null);
  const entryFee = isCrit
    ? (item?.critEntryCost ?? null)
    : (item?.entryFee ?? item?.entryFeeCents ?? null);
  const gameName = item?.gameName || item?.gameId || item?.game || '';
  const mode = item?.mode || '';
  
  // Check if user has a streaming title for this event
  const streamingTitle = item?.myStreamingTitle || item?.streamingTitle || null;
  
  // For finished matches, show result if available
  const myResult = item?.myResult || null;
  const myEarnings = item?.myEarnings || null;

  return (
    <TouchableOpacity 
      onPress={onPress} 
      activeOpacity={0.85} 
      style={styles.card}
    >
      {/* Header Row */}
      <View style={styles.cardHeader}>
        <View style={{ flex: 1 }}>
          <Text style={styles.cardTitle} numberOfLines={2}>{titleOf(item)}</Text>
          <Text style={styles.cardMeta}>
            {gameName}{mode ? ` • ${mode.toUpperCase()}` : ''}
          </Text>
        </View>
        <View style={[styles.badge, { backgroundColor: badgeColors[bucket] || COLORS.MUTED }]}>
          <Text style={styles.badgeText}>{bucket.toUpperCase()}</Text>
        </View>
      </View>

      {/* Info Grid */}
      <View style={styles.infoGrid}>
        {/* Start Time / Countdown */}
        {startMs && (
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>
              {bucket === 'upcoming' ? 'Starts In' : bucket === 'live' ? 'Started' : 'When'}
            </Text>
            <Text style={styles.infoValue}>
              {countdown || formatWhen(startMs)}
            </Text>
          </View>
        )}
        
        {/* Entry Fee: hidden while Crits are OFF (Crit and legacy cash events alike; events are free to enter) */}
        {entryFee != null && critsEnabled && (
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>Entry</Text>
            <Text style={styles.infoValue}>{fmtMoney(entryFee)}</Text>
          </View>
        )}

        {/* Pool: hidden while Crits are OFF (Crit and legacy cash events alike) */}
        {prize != null && critsEnabled && (
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>{isCrit ? 'Crit Pool' : 'Prize Pool'}</Text>
            <Text style={[styles.infoValue, { color: COLORS.GOLD }]}>{fmtMoney(prize)}</Text>
          </View>
        )}
      </View>

      {/* Legacy streaming title hidden 2026-06-17 — stream-title workflow being
          decommissioned (see project_stream_title_legacy memory). */}

      {/* Result - For finished matches */}
      {bucket === 'past' && myResult && (
        <View style={[
          styles.resultBox, 
          myResult === 'win' ? styles.resultWin : styles.resultLoss
        ]}>
          <Text style={styles.resultText}>
            {myResult === 'win' ? '🏆 Victory' : '❌ Defeated'}
            {critsEnabled && myEarnings ? ` • ${fmtMoney(myEarnings)}` : ''}
          </Text>
        </View>
      )}

      {/* Action Row */}
      <View style={styles.actionRow}>
        <TouchableOpacity 
          style={styles.actionButton}
          onPress={onPress}
        >
          <Text style={styles.actionText}>View Details</Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.shareButton}
          onPress={() => onShare(item)}
        >
          <Text style={styles.shareText}>↗ Share</Text>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );
}

// =============================================================================
// RESOLVE ENTRY PARENTS
// =============================================================================
async function resolveEntryParents(entryDocs) {
  const parentRows = [];
  for (const snap of entryDocs) {
    if (!snap?.ref?.parent?.parent) continue;
    const parentRef = snap.ref.parent.parent;
    const parentId = parentRef.id;
    const parentPath = parentRef.path || '';
    
    // Determine collection type from path
    const isTournament = parentPath.startsWith('tournaments/') || parentPath.includes('/tournaments/');
    const kind = isTournament ? 'tournament' : 'match';
    const _collection = isTournament ? 'tournaments' : 'matches';
    
    try {
      const parentSnap = await getDoc(parentRef);
      if (!parentSnap.exists()) continue;
      
      // Get entry-specific data
      const entryData = snap.data() || {};
      
      parentRows.push({
        id: parentId,
        kind,
        _collection, // Store the actual Firestore collection name
        ...parentSnap.data(),
        // Include entry-specific data
        myStreamingTitle: entryData.streamingTitle || null,
        myResult: entryData.result || null,
        myEarnings: entryData.earnings || null,
      });
    } catch (e) {
      console.warn('Failed to resolve parent:', e);
    }
  }
  return parentRows;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function PendingMatchesScreen() {
  const navigation = useNavigation();
  // Evergreen step 2.3: one settings/economy listener, passed to each card.
  const critsEnabled = useCritsEnabled();

  // Auth & Data
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [myTeams, setMyTeams] = useState([]);
  const [myMatches, setMyMatches] = useState([]);
  const [myEventRowsFromEntries, setMyEventRowsFromEntries] = useState([]);

  // UI
  const [activeTab, setActiveTab] = useState('all');
  const [queryText, setQueryText] = useState('');

  const unsub = useRef({
    auth: null,
    teamsMember: null,
    teamsOwner: null,
    entriesPersonal: null,
    entriesTeamById: {},
    matchA: null,
    matchB: null,
  });

  // Navigation helper
  const go = (screen) => {
    const routes = {
      lobby: 'Lobby',
      live: 'LiveMatches',
      yourMatches: 'PendingMatches',
      team: 'YourTeam',
      create: 'CreateTournament',
      how: 'HowToPlay',
      profile: 'Profile',
    };
    navigation.navigate(routes[screen] || screen);
  };

  // Share handler
  const handleShare = async (item) => {
    // Use the _collection field we set, or infer from kind
    const collectionType = item._collection || 
      (item.kind === 'tournament' ? 'tournaments' : 'matches');
    const url = `https://critcash.com/event/${item.id}?collection=${collectionType}`;
    const message = `🏆 ${titleOf(item)}\n\nJoin me on CritCash!\n${url}`;
    
    try {
      if (Platform.OS === 'web' && navigator.share) {
        await navigator.share({ title: titleOf(item), text: message, url });
      } else if (Platform.OS !== 'web') {
        await Share.share({ message, url, title: titleOf(item) });
      } else {
        await navigator.clipboard.writeText(url);
      }
    } catch (e) {
      console.log('Share cancelled or failed');
    }
  };

  // ==========================================================================
  // AUTH LISTENER
  // ==========================================================================
  useEffect(() => {
    unsub.current.auth?.();
    unsub.current.auth = onAuthStateChanged(auth, (user) => {
      setMe(user || null);
      if (!user) setLoading(false);
    });
    return () => unsub.current.auth?.();
  }, []);

  // ==========================================================================
  // TEAMS LISTENER
  // ==========================================================================
  useEffect(() => {
    if (!me?.uid) return;
    setErr('');

    unsub.current.teamsMember?.();
    unsub.current.teamsOwner?.();

    const qMember = query(
      collection(db, 'teams'), 
      where('memberIds', 'array-contains', me.uid), 
      limit(PAGE_SIZE)
    );
    const qOwner = query(
      collection(db, 'teams'), 
      where('ownerId', '==', me.uid), 
      limit(PAGE_SIZE)
    );

    unsub.current.teamsMember = onSnapshot(
      qMember,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data(), kind: 'team' }));
        setMyTeams((prev) => dedupByKindId([...prev, ...rows]));
      },
      (e) => console.warn('Teams (member) error:', e.message)
    );

    unsub.current.teamsOwner = onSnapshot(
      qOwner,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data(), kind: 'team' }));
        setMyTeams((prev) => dedupByKindId([...prev, ...rows]));
      },
      (e) => console.warn('Teams (owner) error:', e.message)
    );

    return () => {
      unsub.current.teamsMember?.();
      unsub.current.teamsOwner?.();
    };
  }, [me?.uid]);

  // ==========================================================================
  // ENTRIES LISTENER
  // ==========================================================================
  useEffect(() => {
    if (!me?.uid) return;
    setErr('');

    unsub.current.entriesPersonal?.();

    // Personal entries
    const qEntriesPersonal = query(
      collectionGroup(db, 'entries'),
      where('userId', '==', me.uid),
      limit(PAGE_SIZE)
    );
    
    unsub.current.entriesPersonal = onSnapshot(
      qEntriesPersonal,
      async (snap) => {
        const parentRows = await resolveEntryParents(snap.docs);
        setMyEventRowsFromEntries((prev) => dedupByKindId([...prev, ...parentRows]));
        setLoading(false);
      },
      (e) => {
        console.warn('Entries (userId) error:', e.message);
        setLoading(false);
      }
    );

    // Team entries
    const liveTeamIds = new Set((myTeams || []).map((t) => t.id));
    for (const [tid, fn] of Object.entries(unsub.current.entriesTeamById)) {
      if (!liveTeamIds.has(tid)) {
        fn?.();
        delete unsub.current.entriesTeamById[tid];
      }
    }
    
    (myTeams || []).forEach((t) => {
      if (unsub.current.entriesTeamById[t.id]) return;
      const qTeamEntries = query(
        collectionGroup(db, 'entries'),
        where('teamId', '==', t.id),
        limit(PAGE_SIZE)
      );
      unsub.current.entriesTeamById[t.id] = onSnapshot(
        qTeamEntries,
        async (snap) => {
          const parentRows = await resolveEntryParents(snap.docs);
          setMyEventRowsFromEntries((prev) => dedupByKindId([...prev, ...parentRows]));
        },
        (e) => console.warn(`Entries (teamId=${t.id}) error:`, e?.message)
      );
    });

    return () => {
      unsub.current.entriesPersonal?.();
    };
  }, [me?.uid, myTeams]);

  // ==========================================================================
  // MATCHES LISTENER
  // ==========================================================================
  useEffect(() => {
    if (!me?.uid) return;
    setErr('');

    unsub.current.matchA?.();
    unsub.current.matchB?.();

    const qMatchA = query(
      collectionGroup(db, 'matches'),
      where('participantUids', 'array-contains', me.uid),
      limit(PAGE_SIZE)
    );
    unsub.current.matchA = onSnapshot(
      qMatchA,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, kind: 'match', ...d.data() }));
        setMyMatches((prev) => dedupByKindId([...prev, ...rows]));
      },
      (e) => console.warn('Matches (participantUids) error:', e.message)
    );

    const qMatchB = query(
      collectionGroup(db, 'matches'),
      where('createdByUid', '==', me.uid),
      limit(PAGE_SIZE)
    );
    unsub.current.matchB = onSnapshot(
      qMatchB,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, kind: 'match', ...d.data() }));
        setMyMatches((prev) => dedupByKindId([...prev, ...rows]));
      },
      (e) => console.warn('Matches (createdByUid) error:', e.message)
    );

    return () => {
      unsub.current.matchA?.();
      unsub.current.matchB?.();
    };
  }, [me?.uid]);

  // ==========================================================================
  // COMBINE & FILTER
  // ==========================================================================
  const combined = useMemo(() => {
    return dedupByKindId([...myEventRowsFromEntries, ...myMatches]);
  }, [myEventRowsFromEntries, myMatches]);

  const filtered = useMemo(() => {
    let out = [...combined];

    // Filter by tab
    if (activeTab !== 'all') {
      out = out.filter((it) => bucketOf(it) === activeTab);
    }

    // Filter by search
    if (queryText.trim()) {
      const q = queryText.toLowerCase();
      out = out.filter((it) => {
        const title = titleOf(it).toLowerCase();
        const game = (it.gameName || it.gameId || '').toString().toLowerCase();
        const mode = (it.mode || '').toLowerCase();
        return title.includes(q) || game.includes(q) || mode.includes(q);
      });
    }

    // Sort by start time (newest first)
    out.sort((a, b) => {
      const tA = toMillis(a?.startAt || a?.startTime) || 0;
      const tB = toMillis(b?.startAt || b?.startTime) || 0;
      return tB - tA;
    });

    return out;
  }, [combined, activeTab, queryText]);

  // Tab counts
  const tabCounts = useMemo(() => {
    const counts = { all: 0, live: 0, pending: 0, upcoming: 0, past: 0 };
    combined.forEach((it) => {
      counts.all++;
      const bucket = bucketOf(it);
      if (counts[bucket] !== undefined) counts[bucket]++;
    });
    return counts;
  }, [combined]);

  // ==========================================================================
  // RENDER
  // ==========================================================================
  
  // Not logged in
  if (!me) {
    return (
      <SafeAreaView style={styles.safe}>
        <Header navigation={navigation} />
        <View style={styles.centerContent}>
          <Text style={styles.emptyIcon}>🎮</Text>
          <Text style={styles.emptyTitle}>Sign in to see your events</Text>
          <Text style={styles.emptyText}>
            Your tournaments, matches, and team events will appear here.
          </Text>
          <TouchableOpacity 
            style={styles.primaryButton}
            onPress={() => navigation.navigate('Login')}
          >
            <Text style={styles.primaryButtonText}>Sign In</Text>
          </TouchableOpacity>
        </View>
        <Footer navigation={navigation} />
        <NavBar 
          navigation={navigation}
          active="yourMatches"
        />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe}>
      <Header navigation={navigation} />

      <View style={styles.container}>
        <Text style={styles.h1}>Your Events</Text>
        
        {/* Quick Stats */}
        {!loading && combined.length > 0 && (
          <View style={styles.quickStats}>
            <View style={styles.quickStatItem}>
              <Text style={styles.quickStatValue}>{tabCounts.live}</Text>
              <Text style={styles.quickStatLabel}>Live</Text>
            </View>
            <View style={styles.quickStatItem}>
              <Text style={styles.quickStatValue}>{tabCounts.upcoming}</Text>
              <Text style={styles.quickStatLabel}>Upcoming</Text>
            </View>
            <View style={styles.quickStatItem}>
              <Text style={styles.quickStatValue}>{tabCounts.pending}</Text>
              <Text style={styles.quickStatLabel}>Pending</Text>
            </View>
          </View>
        )}

        {/* Tabs */}
        <View style={styles.controls}>
          <View style={styles.tabs}>
            {['all', 'live', 'upcoming', 'pending', 'past'].map((t) => (
              <TouchableOpacity
                key={t}
                style={[styles.tabBtn, activeTab === t && styles.tabBtnActive]}
                onPress={() => setActiveTab(t)}
              >
                <Text style={[styles.tabText, activeTab === t && styles.tabTextActive]}>
                  {t.toUpperCase()}
                  {tabCounts[t] > 0 && ` (${tabCounts[t]})`}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <TextInput
            style={styles.search}
            placeholder="Search by title, game, mode..."
            placeholderTextColor={COLORS.MUTED}
            value={queryText}
            onChangeText={setQueryText}
          />
        </View>

        {/* Error */}
        {!!err && (
          <View style={styles.errorBox}>
            <Text style={styles.errorText}>{err}</Text>
          </View>
        )}

        {/* Content */}
        {loading ? (
          <View style={styles.loading}>
            <ActivityIndicator size="large" color={COLORS.GOLD} />
            <Text style={styles.loadingText}>Loading your events…</Text>
          </View>
        ) : (
          <FlatList
            data={filtered}
            keyExtractor={(item) => `${item.kind || 'x'}_${item.id}`}
            contentContainerStyle={{ paddingBottom: 100 }}
            renderItem={({ item }) => {
              // Determine collection - prefer _collection, then infer from kind
              const collectionName = item._collection || 
                (item.kind === 'tournament' ? 'tournaments' : 'matches');
              
              return (
                <EventCard
                  item={{ ...item, _collection: collectionName }}
                  userId={me?.uid}
                  critsEnabled={critsEnabled}
                  onPress={() => {
                    navigation.navigate('EventDetailScreen', { 
                      id: item.id, 
                      collection: collectionName
                    });
                  }}
                  onShare={handleShare}
                />
              );
            }}
            ListEmptyComponent={
              <View style={styles.emptyState}>
                <Text style={styles.emptyIcon}>🎮</Text>
                <Text style={styles.emptyTitle}>
                  {activeTab === 'all' ? 'No events yet' : `No ${activeTab} events`}
                </Text>
                <Text style={styles.emptyText}>
                  {activeTab === 'all' 
                    ? 'Join a tournament or match to see it here!'
                    : `You don't have any ${activeTab} events right now.`}
                </Text>
                <TouchableOpacity 
                  style={styles.primaryButton}
                  onPress={() => navigation.navigate('Lobby')}
                >
                  <Text style={styles.primaryButtonText}>Browse Events</Text>
                </TouchableOpacity>
              </View>
            }
          />
        )}
      </View>

      <Footer navigation={navigation} />
      <NavBar 
        navigation={navigation}
        active="yourMatches"
      />
    </SafeAreaView>
  );
}

// =============================================================================
// STYLES
// =============================================================================
const styles = StyleSheet.create({
  safe: { 
    flex: 1, 
    backgroundColor: COLORS.BG 
  },
  container: { 
    flex: 1, 
    paddingHorizontal: 16, 
    paddingTop: 8 
  },
  centerContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },

  // Header
  h1: {
    color: COLORS.TEXT,
    fontSize: 24,
    fontWeight: '800',
    marginTop: 8,
    marginBottom: 12,
  },

  // Quick Stats
  quickStats: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  quickStatItem: {
    flex: 1,
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    padding: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: COLORS.BORDER,
  },
  quickStatValue: {
    color: COLORS.GOLD,
    fontSize: 24,
    fontWeight: '800',
  },
  quickStatLabel: {
    color: COLORS.MUTED,
    fontSize: 11,
    fontWeight: '600',
    marginTop: 4,
  },

  // Controls
  controls: { 
    marginBottom: 12 
  },
  tabs: { 
    flexDirection: 'row', 
    gap: 8, 
    marginBottom: 12, 
    flexWrap: 'wrap' 
  },
  tabBtn: {
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    backgroundColor: COLORS.CARD,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 20,
  },
  tabBtnActive: { 
    borderColor: COLORS.GOLD, 
    backgroundColor: '#1A1500' 
  },
  tabText: { 
    color: COLORS.MUTED, 
    fontSize: 12, 
    fontWeight: '600' 
  },
  tabTextActive: { 
    color: COLORS.GOLD, 
    fontWeight: '700' 
  },
  search: {
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    backgroundColor: COLORS.CARD,
    color: COLORS.TEXT,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 12,
    fontSize: 14,
  },

  // Loading
  loading: { 
    alignItems: 'center', 
    paddingVertical: 48 
  },
  loadingText: { 
    color: COLORS.MUTED, 
    marginTop: 12 
  },

  // Error
  errorBox: {
    borderWidth: 1,
    borderColor: COLORS.RED,
    backgroundColor: '#1B0E0A',
    padding: 12,
    borderRadius: 10,
    marginBottom: 12,
  },
  errorText: { 
    color: '#FCA5A5' 
  },

  // Empty State
  emptyState: { 
    alignItems: 'center', 
    paddingVertical: 48, 
    paddingHorizontal: 24 
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  emptyTitle: {
    color: COLORS.TEXT,
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyText: {
    color: COLORS.MUTED,
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
  primaryButton: {
    backgroundColor: COLORS.GOLD,
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 10,
  },
  primaryButtonText: {
    color: COLORS.BG,
    fontWeight: '700',
    fontSize: 16,
  },

  // Card
  card: {
    backgroundColor: COLORS.CARD,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  cardTitle: { 
    color: COLORS.TEXT, 
    fontSize: 16, 
    fontWeight: '700', 
    flex: 1, 
    paddingRight: 12 
  },
  cardMeta: {
    color: COLORS.MUTED,
    fontSize: 12,
    marginTop: 4,
  },
  badge: { 
    borderRadius: 8, 
    paddingVertical: 4, 
    paddingHorizontal: 10 
  },
  badgeText: { 
    color: '#FFFFFF', 
    fontSize: 10, 
    fontWeight: '800', 
    letterSpacing: 0.5 
  },

  // Info Grid
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 12,
  },
  infoItem: {
    minWidth: '28%',
  },
  infoLabel: {
    color: COLORS.MUTED,
    fontSize: 11,
    fontWeight: '600',
    marginBottom: 2,
  },
  infoValue: {
    color: COLORS.TEXT,
    fontSize: 14,
    fontWeight: '700',
  },

  // Stream Title Box
  streamTitleBox: {
    backgroundColor: '#1A1500',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: COLORS.GOLD + '30',
  },
  streamTitleLabel: {
    color: COLORS.MUTED,
    fontSize: 11,
    fontWeight: '600',
    marginBottom: 6,
  },
  streamTitleText: {
    color: COLORS.GOLD,
    fontSize: 13,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },

  // Result Box
  resultBox: {
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    alignItems: 'center',
  },
  resultWin: {
    backgroundColor: '#0A1A0A',
    borderWidth: 1,
    borderColor: COLORS.GREEN + '50',
  },
  resultLoss: {
    backgroundColor: '#1A0A0A',
    borderWidth: 1,
    borderColor: COLORS.RED + '50',
  },
  resultText: {
    color: COLORS.TEXT,
    fontSize: 14,
    fontWeight: '700',
  },

  // Actions
  actionRow: {
    flexDirection: 'row',
    gap: 10,
  },
  actionButton: {
    flex: 1,
    backgroundColor: COLORS.GOLD,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  actionText: {
    color: COLORS.BG,
    fontWeight: '700',
    fontSize: 14,
  },
  shareButton: {
    backgroundColor: COLORS.BORDER,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  shareText: {
    color: COLORS.TEXT,
    fontWeight: '600',
    fontSize: 14,
  },
});